import { useState, useEffect } from "react";
import { Calendar, Clock, AlertTriangle, Crown, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { apiCall } from "../App";

const SubscriptionBar = ({ token, user, activeModule }) => {
  const navigate = useNavigate();
  const [subInfo, setSubInfo] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!token || !user || user.role !== "client") return;
    apiCall("GET", "/subscription/my-access", null, token).then(r => {
      if (r.success) setSubInfo(r.data);
    });
  }, [token, user]);

  // Reset dismissed when module changes
  useEffect(() => { setDismissed(false); }, [activeModule]);

  if (!subInfo || user?.role !== "client" || subInfo.subscription_status === "admin") return null;

  // Get per-module data
  const moduleSubs = subInfo.module_subscriptions || {};
  const modData = moduleSubs[activeModule];

  // If no per-module data for this module, show nothing (no subscription for this module)
  const hasModuleSub = modData && modData.status && modData.subscription_end;

  const endDate = hasModuleSub ? new Date(modData.subscription_end) : null;
  const days = hasModuleSub ? modData.days_remaining : null;
  const warning = hasModuleSub ? modData.expiry_warning : null;
  const isExpired = hasModuleSub ? (modData.is_expired || modData.status === "expired") : false;
  const moduleLabel = activeModule === "relationship" ? "Relationship" : "Life Wisdom Coaching";

  // Expiry notification banner
  const showBanner = (warning === "today" || warning === "tomorrow" || isExpired) && !dismissed;

  return (
    <>
      {/* Persistent validity display */}
      {hasModuleSub && !isExpired && modData.status === "premium" && endDate && (
        <div className="flex items-center gap-3 px-4 py-2 rounded-xl bg-cosmic-light/5 border border-divine-gold/10" data-testid="subscription-bar">
          <Calendar className="w-4 h-4 text-divine-gold/60 shrink-0" />
          <div className="flex items-center gap-2 text-xs">
            <span className="text-chakra-crown/50">{moduleLabel}</span>
            <span className={`font-heading ${days <= 3 ? "text-red-400" : days <= 7 ? "text-amber-400" : "text-emerald-400"}`}>
              {days} {days === 1 ? "day" : "days"} left
            </span>
            <span className="text-chakra-crown/30">|</span>
            <span className="text-chakra-crown/50">
              Expires: <span className="text-divine-gold/70">{endDate.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</span>
            </span>
          </div>
          {days <= 7 && (
            <Button size="sm" variant="outline" className="border-divine-gold/20 text-divine-gold hover:bg-divine-gold/10 text-xs ml-auto py-1 h-7"
              onClick={() => navigate("/pricing")} data-testid="renew-btn">
              Renew <ArrowRight className="w-3 h-3 ml-1" />
            </Button>
          )}
        </div>
      )}

      {/* Expiry notification banner */}
      {showBanner && (
        <div className={`rounded-xl p-4 border ${
          isExpired 
            ? "bg-red-500/10 border-red-500/30" 
            : warning === "today" 
              ? "bg-amber-500/10 border-amber-500/30" 
              : "bg-amber-500/5 border-amber-500/20"
        }`} data-testid="expiry-banner">
          <div className="flex items-start gap-3">
            <AlertTriangle className={`w-5 h-5 shrink-0 mt-0.5 ${isExpired ? "text-red-400" : "text-amber-400"}`} />
            <div className="flex-1">
              <p className={`font-heading text-sm ${isExpired ? "text-red-400" : "text-amber-400"}`}>
                {isExpired 
                  ? `Your ${moduleLabel} Subscription Has Expired` 
                  : warning === "today" 
                    ? `Your ${moduleLabel} Subscription Expires Today!` 
                    : `Your ${moduleLabel} Subscription Expires Tomorrow`}
              </p>
              <p className="text-chakra-crown/60 text-xs mt-1">
                {isExpired
                  ? `Your access to the ${moduleLabel} module has been suspended. Renew now to continue your transformation journey.`
                  : warning === "today"
                    ? "After midnight, your access will be suspended. Renew now to avoid interruption."
                    : "Please renew your subscription to continue uninterrupted access."}
              </p>
              <div className="flex items-center gap-2 mt-3">
                <Button size="sm" className="bg-divine-gold text-cosmic-void hover:bg-divine-gold-light text-xs"
                  onClick={() => navigate("/pricing")} data-testid="renew-now-btn">
                  <Crown className="w-3 h-3 mr-1" /> Renew Now
                </Button>
                {!isExpired && (
                  <Button size="sm" variant="ghost" className="text-chakra-crown/40 text-xs"
                    onClick={() => setDismissed(true)} data-testid="dismiss-expiry-btn">
                    Remind me later
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Expired state — full block */}
      {isExpired && (
        <div className="rounded-xl p-6 border border-red-500/30 bg-red-500/5 text-center" data-testid="expired-block">
          <Crown className="w-10 h-10 text-red-400/60 mx-auto mb-3" />
          <h3 className="font-heading text-lg text-red-400">{moduleLabel} Subscription Expired</h3>
          <p className="text-chakra-crown/50 text-sm mt-2 max-w-md mx-auto">
            Your {moduleLabel} subscription expired on {endDate?.toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}. 
            Renew to regain access and continue your 3A transformation journey.
          </p>
          <Button className="bg-divine-gold text-cosmic-void hover:bg-divine-gold-light mt-4"
            onClick={() => navigate("/pricing")} data-testid="expired-renew-btn">
            <Crown className="w-4 h-4 mr-2" /> Choose a Plan & Renew
          </Button>
        </div>
      )}
    </>
  );
};

export default SubscriptionBar;
